import '../ActivityActions.scss';
import '../../Signing.scss';
import React, { useEffect, useRef, useState } from 'react';
import { IBaseWithSignProps, ICheckTableRow, ICheckTableSchema } from './IBaseWithSignProps';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@atoms/AttachFilesBlock/AttachFilesBlock';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { IActivityActionModel } from '@models/document/activityExecutors/IActivityActionModel';
import { ICertificate } from '@models/certificates/cert';
import { IDocAttachesVersions } from '@models/attaches/IDocAttachesVersions';
import { useActions } from '@hooks/useActions';
import { CadespluginService } from '@services/CadespluginService';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { getActivityIdFromCompositeId } from '@utils/helpers';
import { Buffer } from 'buffer';
import { IDocumentData } from '@models/document/IDocumentData';
import { IField, IFieldElem } from '@models/IFormData';
import { DocumentService } from '@services/DocumentService';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { AutoTaskService } from '@services/actions/AutoTaskService';
import { ISelectedFiles } from '@atoms/AttachFilesBlock/ISelectedFiles';
import { checkAttachValidation } from '@utils/documentUtils';
import { IPartsSign } from '@models/attaches/IAttachViewSignModel';
import Modal from '@atoms/Modal';
import clsx from 'clsx';
import Button from '@atoms/Button';
import { MdClose } from 'react-icons/md';
import Preloader from '@atoms/Preloader';
import Hint from '@atoms/Hint';
import Box from '@atoms/Box';
import Moment from 'moment/moment';
import FatalError from '@molecules/FatalError';
import FatalErrorIcon from '@molecules/FatalError/FatalErrorIcon';
import { API_URL } from '@/http';
import Checkbox from '@atoms/Checkbox';
import Textarea from '@atoms/Textarea';
import { STORAGE_KEYS } from '@/constants';

export const BaseWithSignModal: React.FC<IBaseWithSignProps> = (props: IBaseWithSignProps) => {
    const currentDocData = useTypedSelector((state) => state.document.data);

    const childFilesBlock = useRef<IAttachFilesBlockRefActions | null>(null);
    const [attachesCreateModel, setAttachesCreateModel] = useState<IAttachesCreateModel>();
    const [actionModel, setActionModel] = useState<IActivityActionModel>();
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);

    const [comment, setComment] = useState<string>();
    const [selectedCertThumb, setSelectedCertThumb] = useState<string>();

    const [errorText, setErrorText] = useState<string>();
    const [warningText, setWarningText] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingText, setLoadingText] = useState<string>();

    const [certList, setCertList] = useState<ICertificate[]>();

    const [attachesRead, setAttachesRead] = useState<boolean>(false);
    const [attaches, setAttaches] = useState<IDocAttachesVersions[]>();

    const { fetchDocumentData } = useActions();

    const loadCertificates = async () => {
        setLoadingText('Загрузка сертификатов...');
        setLoading(true);
        let serv = new CadespluginService();
        //Если имя активити и поток документа совпадают с нужными кейсами из конфига - ставим алгоритм ГОСТ
        let alg: string[] = [props.defaultAlg];
        let gostCN: string[] = [];
        if (
            props.docFlow &&
            props.gostCases.find(
                (x) =>
                    x.flowName == props.docFlow &&
                    x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase(),
            )
        ) {
            gostCN = props.gostCN;
            alg = ['гост', 'gost'];
        }

        let certs = await serv.getCertListByAlgSubstring(alg, gostCN);

        //если серификат один - сразу делаем его вбыранным
        if (certs && certs.length == 1) {
            setSelectedCertThumb(certs[0].thumbprint);
        }

        setCertList(certs);

        setLoading(false);
    };

    useEffect(() => {
        loadCertificates();

        setLoadingText('Загрузка настроек...');
        setLoading(true);
        let activityExecutionService = new ActivityExecutionService();
        activityExecutionService.getActivityActionModel(props.actionKey, props.activityId).then((res) => {
            setActionModel(res.data);
            setComment(res.data.prefilledComment);
            setLoading(false);
        });

        //обновление контекста документа, т.к. в поисковых папках нет контекста документа
        if (!currentDocData || props.docId !== currentDocData.id) {
            fetchDocumentData(props.docId, undefined);
        }

        //получение модели для аттачей
        if (props.attachFiles) {
            let attachServ = new AttachActionsService(props.docId, '-1');
            attachServ.getAttachInfo(props.actionKey, props.activityId).then((res) => {
                setAttachesCreateModel(res.data);
            });
            if (props.previewAttachFiles) {
                setLoading(true);
                let actId = getActivityIdFromCompositeId(props.objId);
                setLoadingText('Получение перечня файлов...');
                attachServ.getDocAttachKeysByActKey(actId, props.actionKey).then((res) => {
                    setAttaches(res.data);
                    setAttachesRead(false);
                    setLoading(false);
                });
            } else {
                setAttachesRead(true);
            }
        }
    }, []);

    const encodeBase64 = (str: string): string => Buffer.from(str, 'binary').toString('base64');

    const writeCheckResultToTable = async (
        schema: ICheckTableSchema,
        rowsToAdd: ICheckTableRow[],
        clonedDocData: IDocumentData,
    ) => {
        //let clonedDocData: IDocumentData = JSON.parse(JSON.stringify(currentDocData));
        let tbl: IField | undefined = clonedDocData.fields.find((x) => x.name == schema.name);
        if (tbl) {
            let arr: any[] = tbl.value as any[];
            if (arr) {
                let maxNum = Math.max(...arr.map((x) => x['|NUM'] as number));
                if (maxNum == Number.NEGATIVE_INFINITY) maxNum = 0;

                rowsToAdd.forEach((x) => {
                    maxNum = maxNum + 1;
                    let newCheckRow: any = {
                        '|NUM': maxNum,
                    };
                    newCheckRow[schema.systemCol] = x.system;
                    newCheckRow[schema.resultCol] = x.result;
                    newCheckRow[schema.dateCol] = new Date();
                    arr.push(newCheckRow);
                });
            }

            setLoadingText('Запись результатов проверок...');
            clonedDocData.ignoreHooks = true;
            await DocumentService.saveData(clonedDocData, undefined);
        }
    };

    const checkConditionFormula = async (formula: string): Promise<boolean> => {
        let formulaMgr = new FormulaManager(formula);
        let formulaData: Record<string, IFieldElem> = {} as Record<string, IFieldElem>;
        currentDocData?.fields.forEach(
            (x) =>
                (formulaData[x.name] = {
                    index: 0,
                    name: '',
                    value: x.value,
                }),
        );
        formulaMgr.Init(formulaData);
        return await formulaMgr.EvalFormulaValues(true, false);
    };

    const getFieldByName = (data: IDocumentData | undefined, value: string): IField | undefined => {
        return data?.fields.find((_) => _.name === value);
    };

    const getFieldsByNames = (data: IDocumentData | undefined, values: string | string[]): IField[] => {
        let fields: IField[] = [];

        if (Array.isArray(values)) {
            values.forEach((item) => {
                let field = getFieldByName(data, item);
                if (field) fields.push(field);
            });
        } else {
            let field = getFieldByName(data, values);
            if (field) fields.push(field);
        }

        return fields;
    };

    const onSubmit = async () => {
        setLoading(true);

        let attachServ = new AttachActionsService(props.docId, '-1');
        let cadesServ = new CadespluginService();

        let clonedDocData: IDocumentData = JSON.parse(JSON.stringify(currentDocData));

        let error: { flag: boolean; msg?: string } = {
            flag: false,
            msg: '',
        };

        //Проверка СНИЛС сертификата и дока прямо на фронте перед подписанием
        if (props.certDataChecks && selectedCertThumb) {
            let selectedCert = certList?.find((x) => x.thumbprint == selectedCertThumb);
            if (selectedCert) {
                setLoadingText('Проверка СНИЛС сертификата...');
                let resultTableSchema = props.certDataChecks.resultTableSchema;
                for (let i = 0; i < props.certDataChecks.conditionChecks.length; i++) {
                    let check = props.certDataChecks.conditionChecks[i];

                    //определить по условию, нужна ли проверка
                    let checkIsOk: boolean = false;
                    for (let x of check.conditions) {
                        if (
                            x.flowName == props.docFlow &&
                            x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase() &&
                            (!x.formula || (await checkConditionFormula(x.formula))) //или формула не задачи или она "выполняется"
                        )
                            checkIsOk = true;
                    }
                    if (!checkIsOk) continue;

                    let partNames = check.frontCheck.certPartNames.split(',').map((x) => x.trim());
                    let docRequisite = currentDocData?.fields.find((x) => x.name == check.frontCheck.docRequisite);

                    if (docRequisite) {
                        let docRequisiteValue = docRequisite.value as string;
                        let valuesFromCert = partNames
                            .map((part) => cadesServ.getCnPart(selectedCert!.rawSubject, part))
                            .filter((x) => x);

                        let checkRowsToWrite: ICheckTableRow[] = [];

                        if (valuesFromCert.length > 0 && docRequisiteValue) {
                            //если вообще есть что сравнивать (непустые значения у дока и у сертификата)

                            let isSnilsCheckFailed: boolean =
                                valuesFromCert.filter((x) => x == docRequisiteValue).length == 0;

                            checkRowsToWrite.push({
                                system: check.frontCheck.systemVal,
                                result: isSnilsCheckFailed
                                    ? check.frontCheck.resultFailVal
                                    : check.frontCheck.resultOkVal,
                            });

                            if (isSnilsCheckFailed) {
                                await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);
                                setErrorText(check.frontCheck.errorText);
                                setLoading(false);
                                return;
                            }
                        }

                        if (check.servCheck?.runAutoTaskNameToCheck) {
                            let dataToSign = currentDocData?.id;
                            if (dataToSign) {
                                setLoadingText('Проверка в СС ЭП...');
                                let encodedDataToSign = encodeBase64(dataToSign);

                                const signData = await cadesServ.signData(encodedDataToSign, selectedCertThumb!);

                                let serv = new AutoTaskService();

                                try {
                                    let res = await serv.runAutoImport({
                                        name: check.servCheck.runAutoTaskNameToCheck,
                                        docKey: currentDocData?.id,
                                        parameters: [encodedDataToSign, signData.sign],
                                    });

                                    checkRowsToWrite.push({
                                        system: check.servCheck.systemVal,
                                        result: res.data.isSucceded
                                            ? check.servCheck.resultOkVal
                                            : `${check.servCheck.resultFailVal}. ${res.data.message}`,
                                    });

                                    if (!res.data.isSucceded) {
                                        error.flag = true;
                                        error.msg = res.data.message;
                                    }
                                } catch (e) {
                                    error.flag = true;
                                    error.msg = e as string;
                                }
                            }
                        }

                        if (checkRowsToWrite.length > 0)
                            await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);

                        if (error.flag) {
                            setErrorText(error.msg);
                            setLoading(false);
                            return;
                        }
                    }
                }
            }
        }

        //Проверка МЧД по аналогии со СНИЛС
        if (props.mchdDataChecks) {
            let mchdDataChecks = props.mchdDataChecks;
            let resultTableSchema = mchdDataChecks.resultTableSchema;
            for (let i = 0; i < mchdDataChecks.conditionChecks.length; i++) {
                let check = mchdDataChecks.conditionChecks[i];

                //определить по условию, нужна ли проверка
                let checkIsOk: boolean = false;
                for (let x of check.conditions) {
                    if (
                        x.flowName == props.docFlow &&
                        x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase() &&
                        (!x.formula || (await checkConditionFormula(x.formula))) //или формула не задачи или она "выполняется"
                    ) {
                        checkIsOk = true;
                    }
                }
                if (!checkIsOk) continue;

                if (check.servCheck.runAutoTaskNameToCheck) {
                    setLoadingText('Проверка в МЧД...');
                    let docRequisites = getFieldsByNames(currentDocData, check.servCheck.docRequisite);
                    if (docRequisites.length > 0) {
                        let serv = new AutoTaskService();
                        let parameters = docRequisites.map((item) => {
                            return item.value as string;
                        });

                        let checkRowsToWrite: ICheckTableRow[] = [];

                        try {
                            let res = await serv.runAutoImport({
                                name: check.servCheck.runAutoTaskNameToCheck,
                                docKey: currentDocData?.id,
                                parameters: parameters,
                            });

                            checkRowsToWrite.push({
                                system: check.servCheck.systemVal,
                                result: res.data.isSucceded
                                    ? check.servCheck.resultOkVal
                                    : `${check.servCheck.resultFailVal}. ${res.data.message}`,
                            });

                            if (!res.data.isSucceded) {
                                error.flag = true;
                                error.msg = res.data.message;
                            }
                        } catch (e) {
                            error.flag = true;
                            error.msg = e as string;
                        }

                        if (checkRowsToWrite.length > 0)
                            await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);

                        if (error.flag) {
                            setErrorText(error.msg);
                            setLoading(false);
                            return;
                        }
                    }
                }
            }
        }

        try {
            if (props.objId && selectedCertThumb) {
                let filesToSend: ISelectedFiles = { files: [], attachProperties: [] };

                if (attachesCreateModel && childFilesBlock) {
                    if (!childFilesBlock?.current?.isValid()) {
                        setLoading(false);
                        return;
                    }
                    let f = childFilesBlock?.current?.getData();
                    if (f) filesToSend = f;
                }

                // проверка на обязательность наличия аттачей
                if (
                    props.attachFiles &&
                    props.attachRequired &&
                    (filesToSend?.files === undefined || filesToSend?.files.length === 0)
                ) {
                    setWarningText('Необходимо приложить файл');
                    setLoading(false);
                    return;
                }

                if (props.attachRequiredValidation && props.attachRequiredValidation.length > 0) {
                    let checkResult = checkAttachValidation(props.attachRequiredValidation, filesToSend);
                    if (checkResult.length > 0) {
                        let text = checkResult.join('\n');
                        setWarningText(text);
                        setLoading(false);
                        return;
                    }
                }

                if (filesToSend?.files?.length > 0) {
                    await attachServ.createAttaches(filesToSend);
                }

                let actId = getActivityIdFromCompositeId(props.objId);

                setLoadingText('Получение перечня файлов...');
                let attachesData = attaches
                    ? attaches
                    : (await attachServ.getDocAttachKeysByActKey(actId, props.actionKey)).data;

                for (let index = 0; index < attachesData.length; index++) {
                    const attachData = attachesData[index];

                    let docId = attachData.docId;
                    let attachesIds = attachData.attaches;

                    let signsForAttaches: IPartsSign[] = []; //: {[attachId : string] : [sign: string, algorithm: string]}={};
                    //по всем аттачам
                    for (let i = 0; i < attachesIds.length; i++) {
                        let attachId = attachesIds[i];

                        setLoadingText(`Получение файла №${i + 1}...`);
                        let base64content = await attachServ.getAttachContentInBase64(docId, attachId.attachId);

                        setLoadingText(`Подпись файла №${i + 1}...`);
                        const signWithAlg = await cadesServ.signData(base64content, selectedCertThumb!);

                        signsForAttaches.push({
                            algorithm: signWithAlg.algorithm,
                            sign: signWithAlg.sign,
                            attachId: attachId.attachId,
                            docId: docId,
                        });
                    }

                    setLoadingText(`Отправка на сервер...`);

                    let activityExecutionService = new ActivityExecutionService();
                    await activityExecutionService.activityActionWithSign(
                        props.actionKey,
                        actId,
                        signsForAttaches,
                        props.approveModel.actType,
                        comment,
                    );
                }
                setLoading(false);
                if (props.completeHandler) props.completeHandler(true);
                return;
            }
        } catch (e: any) {
            if (typeof e === 'string') {
                setErrorText(e);
            } else if (Array.isArray(e)) {
                const errorText = e.join('\n');
                setErrorText(errorText);
            } else {
                const errorText =
                    'Ошибка при подписании. Убедитесь, что в браузере установлен и настроен плагин КриптоПро ЭЦП. ' +
                    'Проверить работу плагина: https://cryptopro.ru/sites/default/files/products/cades/demopage/cades_bes_sample.html';
                setErrorText(errorText);
            }
            console.error(e);
        }

        setLoading(false);

        // attaches.data.attaches.forEach((attachId,i) => {
        //     console.log(`attach = ${attachId}`);
        // });

        // let serv = new CadespluginService();
        // if(selectedCertThumb){
        //     let sign = await serv.signData('0KLQtdGB0YLQvtCy0LDRjyDRgdGC0YDQvtC60LAgMTIz', selectedCertThumb);
        //     console.log(sign);
        // }

        //setTimeout(() => { setLoading(false) }, 1000);
    };

    const onCertSelect = (e: any) => {
        if (e.target.value) setSelectedCertThumb(e.target.value);
    };

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            header={
                <div className={clsx('box')}>
                    <div>
                        <span>{props.displayName}</span>
                    </div>
                    <div className={clsx('left')}>
                        <Button
                            buttonType="text"
                            textColor="neutral"
                            size="xs"
                            aria-label="Закрыть окно"
                            onClick={() => {
                                if (props?.completeHandler) props?.completeHandler(false);
                            }}
                            startAdornment={<MdClose size="24" />}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        disabled={
                            (props.previewAttachFiles && !attachesRead) ||
                            (props.approveModel.commentIsRequired &&
                                (comment === undefined || comment?.length === 0)) ||
                            selectedCertThumb === undefined ||
                            loading ||
                            filesSizeExceeded
                        }
                        onClick={async () => {
                            await onSubmit();
                        }}
                        size="s"
                        aria-label={props.okButtonText}
                    >
                        {props.okButtonText}
                    </Button>
                    <Button
                        onClick={() => {
                            if (props?.completeHandler) props?.completeHandler(false);
                        }}
                        buttonType="light"
                        size="s"
                        aria-label={props.cancelButtonText}
                        disabled={loading}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            {loading ? (
                <div>
                    <Preloader size="m" position="static" />
                    {loadingText}
                </div>
            ) : (
                <>
                    {errorText && <Hint title={`Ошибка: ${errorText}`} variant="red" maxWidth={'100%'} />}
                    {warningText && <Hint title={`Внимание: ${warningText}`} variant="yellow" maxWidth="100%" />}
                    <div className="certList">
                        {certList && certList.length > 0 ? (
                            certList.map((cert, i) => (
                                <Box key={i.toString()} className="certificateBox">
                                    <input
                                        type="radio"
                                        name="certSelection"
                                        value={cert.thumbprint}
                                        onChange={onCertSelect}
                                        checked={cert.thumbprint == selectedCertThumb}
                                    ></input>
                                    <div>
                                        <div>
                                            <b>Владелец сертификата </b>
                                            {cert.subjectName}
                                        </div>
                                        <div>
                                            <b>Центр выдачи сертификата </b>
                                            {cert.issuerName}
                                        </div>
                                        <div>
                                            <b>Действителен с </b>
                                            {Moment(cert.validFrom).format('DD.MM.YYYY')}
                                        </div>
                                        <div>
                                            <b>Действителен по </b>
                                            {Moment(cert.validTo).format('DD.MM.YYYY')}
                                        </div>
                                    </div>
                                </Box>
                            ))
                        ) : (
                            <div className="certList--noCertsAvailable">
                                <FatalError
                                    icon={<FatalErrorIcon variant="default" size={{ width: 100, height: 100 }} />}
                                    title="Нет доступных сертификатов"
                                    description=""
                                />
                            </div>
                        )}

                        {attaches && props.previewAttachFiles ? (
                            <div className="attachesList">
                                {attaches.map((docAttaches) => {
                                    return docAttaches.attaches?.map((attach, i) => {
                                        let link = `${API_URL}/attachment/download/{documentKey}/{key}?access_token=${localStorage.getItem(
                                            STORAGE_KEYS.ACCESS_TOKEN,
                                        )}`;

                                        link = link.replace('{key}', attach.attachId.split('_')[0]);
                                        link = link.replace('{documentKey}', docAttaches.docId.toString());
                                        return (
                                            <Box key={i.toString()} className="attachesBox">
                                                <div>
                                                    <div>
                                                        <b>
                                                            <a target="_blank" href={link} rel="noopener,noreferrer">
                                                                {attach.title}
                                                            </a>
                                                        </b>
                                                    </div>
                                                </div>
                                            </Box>
                                        );
                                    });
                                })}
                                <div>
                                    <Checkbox
                                        defaultChecked={false}
                                        onChange={() => {
                                            setAttachesRead(!attachesRead);
                                        }}
                                        label={'С файлами ознакомлен'}
                                    />
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>

                    <div>
                        <Textarea
                            className={clsx(
                                props.approveModel.commentIsRequired &&
                                    (comment == undefined || comment.length == 0) &&
                                    'invalid',
                            )}
                            initialRowCount={8}
                            placeholder={'Комментарий' + (props.approveModel.commentIsRequired ? ' (обязательно)' : '')}
                            defaultValue={actionModel?.prefilledComment}
                            required={props.approveModel.commentIsRequired}
                            onChange={(e: any) => {
                                setComment(e.target.value);
                            }}
                        />
                    </div>
                    <div>
                        {attachesCreateModel && (
                            <AttachFilesBlock
                                ref={childFilesBlock}
                                model={attachesCreateModel}
                                onChangeFilesSizeExceeded={(value) => {
                                    setFilesSizeExceeded(value);
                                }}
                            />
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};
